/**
 * FaceTracker — 468-landmark face mesh via MediaPipe Face Landmarker.
 *
 * Same lazy-load + lifecycle pattern as PoseTracker. Exposes:
 *
 *   - getLandmark('leftEye')           — friendly-name lookup
 *   - getLandmarkByIndex(467)          — full 468-point access
 *   - blendshapes                      — 52-channel expression weights
 *                                        (smile, blink, jawOpen, etc.) when
 *                                        the face landmarker is configured
 *                                        with output_face_blendshapes
 *   - facialTransform                  — 4×4 head pose matrix
 *   - getMouthOpenness()               — convenience derived metric
 *   - getEyeOpenness('left'|'right')   — convenience derived metric
 */
import { BaseTracker } from './BaseTracker.js';
import {
  FACE_LANDMARKS,
  FACE_LANDMARK_COUNT,
  lookupLandmark,
  distance
} from './landmarks.js';

const DEFAULT_CDN =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/vision_bundle.mjs';
const DEFAULT_WASM =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm';
const DEFAULT_MODEL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

export class FaceTracker extends BaseTracker {
  /**
   * @param {object} [opts]
   * @param {string} [opts.modelUrl]
   * @param {string} [opts.libraryUrl]
   * @param {string} [opts.wasmUrl]
   * @param {number} [opts.numFaces=1]
   * @param {boolean} [opts.outputBlendshapes=true]
   * @param {boolean} [opts.outputTransformMatrix=true]
   */
  constructor(opts = {}) {
    super();
    this.modelUrl = opts.modelUrl || DEFAULT_MODEL;
    this.libraryUrl = opts.libraryUrl || DEFAULT_CDN;
    this.wasmUrl = opts.wasmUrl || DEFAULT_WASM;
    this.numFaces = opts.numFaces || 1;
    this.outputBlendshapes = opts.outputBlendshapes !== false;
    this.outputTransformMatrix = opts.outputTransformMatrix !== false;

    this._landmarker = null;
    this._video = null;
    this._rafId = null;
  }

  async load() {
    if (this.isAvailable || this.isLoading) return;
    this.isLoading = true;
    try {
      const vision = await import(/* @vite-ignore */ this.libraryUrl);
      const { FaceLandmarker, FilesetResolver } = vision;
      const fileset = await FilesetResolver.forVisionTasks(this.wasmUrl);
      this._landmarker = await FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: this.modelUrl, delegate: 'GPU' },
        runningMode: 'VIDEO',
        numFaces: this.numFaces,
        outputFaceBlendshapes: this.outputBlendshapes,
        outputFacialTransformationMatrixes: this.outputTransformMatrix
      });
      this.isAvailable = true;
    } catch (err) {
      this.lastError = err;
      throw err;
    } finally {
      this.isLoading = false;
    }
  }

  async start(videoElement) {
    if (this._destroyed) return;
    if (!this.isAvailable) await this.load();
    this._video = videoElement;
    this._loop();
  }

  stop() {
    if (this._rafId != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this._rafId);
    }
    this._rafId = null;
    if (this._landmarker && typeof this._landmarker.close === 'function') {
      try { this._landmarker.close(); } catch (_) { /* ignore */ }
    }
    this._landmarker = null;
    this.isAvailable = false;
  }

  _loop() {
    if (this._destroyed || !this._video || !this._landmarker) return;
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    try {
      if (this._video.readyState >= 2) {
        const result = this._landmarker.detectForVideo(this._video, now);
        if (result && result.faceLandmarks && result.faceLandmarks.length > 0) {
          this._emit({
            landmarks: result.faceLandmarks[0],
            blendshapes: result.faceBlendshapes ? result.faceBlendshapes[0] : null,
            transform: result.facialTransformationMatrixes
              ? result.facialTransformationMatrixes[0]
              : null,
            timestamp: now
          });
        }
      }
    } catch (_) { /* ignore frame errors */ }
    if (typeof requestAnimationFrame !== 'undefined') {
      this._rafId = requestAnimationFrame(() => this._loop());
    }
  }

  // ─────────────────────────────────────────────────────────── lookups

  getLandmark(name) {
    if (!this.latest || !this.latest.landmarks) return null;
    const idx = lookupLandmark(FACE_LANDMARKS, name);
    if (idx < 0 || idx >= FACE_LANDMARK_COUNT) return null;
    return this.latest.landmarks[idx] || null;
  }

  getLandmarkByIndex(idx) {
    if (!this.latest || !this.latest.landmarks) return null;
    if (idx < 0 || idx >= FACE_LANDMARK_COUNT) return null;
    return this.latest.landmarks[idx] || null;
  }

  /**
   * Look up a blendshape weight by name (e.g. 'mouthSmileLeft', 'eyeBlinkLeft',
   * 'jawOpen'). Returns 0..1 or null if blendshapes weren't requested.
   */
  getBlendshape(name) {
    if (!this.latest || !this.latest.blendshapes) return null;
    const cats = this.latest.blendshapes.categories || [];
    for (const c of cats) {
      if (c.categoryName === name) return c.score;
    }
    return null;
  }

  /** Vertical mouth opening normalized by face height. */
  getMouthOpenness() {
    const top = this.getLandmark('upperLipTop');
    const bot = this.getLandmark('lowerLipBottom');
    const fHigh = this.getLandmark('foreheadCenter');
    const fLow = this.getLandmark('chinBottom');
    if (!top || !bot || !fHigh || !fLow) return 0;
    const lipDist = distance(top, bot);
    const faceDist = distance(fHigh, fLow) || 1;
    return lipDist / faceDist;
  }

  /** Vertical eye opening for either eye, normalized by eye width. */
  getEyeOpenness(side) {
    const top = this.getLandmark(side === 'right' ? 'rightEyeTop' : 'leftEyeTop');
    const bot = this.getLandmark(side === 'right' ? 'rightEyeBottom' : 'leftEyeBottom');
    const inner = this.getLandmark(side === 'right' ? 'rightEyeInner' : 'leftEyeInner');
    const outer = this.getLandmark(side === 'right' ? 'rightEyeOuter' : 'leftEyeOuter');
    if (!top || !bot || !inner || !outer) return 0;
    const v = distance(top, bot);
    const w = distance(inner, outer) || 1;
    return v / w;
  }

  static get LANDMARKS() { return FACE_LANDMARKS; }
}
